import {
    BooleanBadge,
    CenterCircularProgress,
    LazyBreadcrumb,
    Toolbar,
    useModal,
} from '@europrocurement/flexy-components';
import {
    antennesSelectorMain,
    AntennesUtilisateurApiObject,
    useUtilisateurService,
    useUtilisateursServiceSelector,
    UtilisateursAppDispatch,
} from '@europrocurement/l2d-domain';
import { Container } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { AntennesJsonldAntenneUpdate } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { B2DLogoIcon } from '@europrocurement/l2d-icons';
import {
    errorNotification,
    successNotification,
} from '@europrocurement/flexy-components/components/atoms/NotificationToasters';
import { ValidationModal } from '@europrocurement/l2d-modules/modules/ValidationModal/ValidationModal';
import { antennesListePath, utilisateursBasePath } from '../../constants/paths';
import { AntennaCard } from '../../components/Antenne/AntennaCard/AntennaCard';
import { ErrorPageGeneric } from '../../ErrorPageGeneric';

/**
 * Display the antenna details Card and quick delete button
 * @returns Toolbar and AntennaCard components
 */
export const AntenneFiche: React.FunctionComponent = function () {
    const { idAntenne } = useParams();
    const dispatch = useDispatch<UtilisateursAppDispatch>();
    const { modalActions } = useModal();

    // instanciate variables to dispatch data
    const { selectAntennes, antennesApi } = useUtilisateurService();
    const dataSource = useUtilisateursServiceSelector(antennesSelectorMain);
    const antenne = dataSource.selected;

    // useState will be used to update data on front rendering
    const [antennaData, setAntennaData] = useState<AntennesUtilisateurApiObject>();

    useEffect(
        () => {
            if (idAntenne) {
                dispatch({ type: '/utilisateur/antennes/deletemainSelected' });
                dispatch(selectAntennes({ id: idAntenne }));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [], // on mount only
    );

    // Is component ready to render content?
    useEffect(() => {
        const antennaLoaded = idAntenne && antenne && `${antenne.idAntenne}` === idAntenne;
        const dataSourceLoaded = dataSource.selectedStatus === 'succeeded';

        // if the user is the right one and his data have been retrieved, put them to the state
        if (antennaLoaded && dataSourceLoaded) {
            setAntennaData(antenne);
        }
    }, [antenne, dataSource.selectedStatus, idAntenne]);

    if (!idAntenne) {
        return null;
    }

    if (dataSource.selectedStatus === 'failed') {
        return (
            <ErrorPageGeneric
                message="Antenne introuvable"
                statusCode={404}
                title="Page introuvable"
                description="This is Not Found page"
                details="L'identifiant saisit ne correspond à aucune antenne. Merci de vérifier votre saisie."
                returnButtonLabel="Retour à la liste des antennes"
                returnUrl="/utilisateurs/antennes/"
                logo={<B2DLogoIcon style={{ height: '64px', marginTop: '40px' }} />}
            />
        );
    }

    // render circular progress until data is loaded
    if (!antennaData) {
        return <CenterCircularProgress sx={{ height: '70vh' }} />;
    }

    /**
     * Get payload as params and send it to the API on PATCH method
     * taster fired on success and error of the request
     * @param payload antenna data from the form
     */
    const handlePatch = (payload: AntennesJsonldAntenneUpdate) => {
        antennesApi
            .apiAntennesIdAntennePatch({
                idAntenne,
                antennesAntenneUpdate: payload,
            })
            .then(() => {
                setAntennaData(payload as AntennesUtilisateurApiObject);
                successNotification("L'antenne a bien été modifiée.");
            })
            .catch(errorNotification);
    };

    const breadcrumbPath = [
        {
            path: 'antennes',
            link: `/${utilisateursBasePath}/${antennesListePath}`,
        },
        `antenne de  ${antennaData.libelle}`,
        '',
    ];

    const handleModalSubmit = (payload: AntennesJsonldAntenneUpdate) => {
        handlePatch(payload);
        modalActions.close();
    };

    const handleValidationModal = (payload: AntennesJsonldAntenneUpdate, value: boolean) => {
        const status = value ? 'activer' : 'désactiver';
        const message = `Êtes-vous sûr de vouloir ${status} l'antenne ?`;

        modalActions.call(
            <ValidationModal
                message={message}
                onSubmit={() => handleModalSubmit(payload)}
            />,
        );
    };

    return (
        <Container maxWidth="lg">
            <Toolbar>
                <LazyBreadcrumb path={breadcrumbPath} />
                <BooleanBadge
                    label={antennaData.isDeleted ? 'Antenne désactivée' : 'Antenne active'}
                    value={!antennaData.isDeleted}
                    icon
                    toggleAction={() =>
                        handleValidationModal(
                            {
                                idAntenne: parseInt(idAntenne, 10),
                                libelle: antennaData.libelle,
                                isDeleted: !antennaData.isDeleted,
                            },
                            antennaData.isDeleted,
                        )
                    }
                />
            </Toolbar>

            <AntennaCard
                antenne={antennaData}
                handlePatch={handlePatch}
            />
        </Container>
    );
};
